<script setup lang="ts">
import type { Item } from './SavedAnalysisStateBrowser.vue';
import type { BatchResult } from '@/src/components/segmentation/segmentation-typing';
import type { Json } from '@/supabase/types';

import { useActiveStudyStore } from '@src/store/active-study';
import { seriesClassification } from '@src/store/dicom-web/SeriesClassification';
import LZString from 'lz-string';
import { ref, useTemplateRef } from 'vue';

import { useAlert } from '@/src/composables/useAlert';
import { useBackendSegmentation } from '@/src/composables/useBackendSegmentation';
import { session, supabase } from '@/src/supabase';

import { SerializedAnalysisStateSchema, useAppState } from '../store/ocg-app-state';
import { getMessage } from '../utils/exception';
import SavedAnalysisStateBrowser from './SavedAnalysisStateBrowser.vue';

const SYSTEM_SAVE_VERSION = 'system-auto-v1';
const SAVE_WITH_RESULT_TITLE = 'Completed Analysis';
const AUTOSAVE_TITLE = 'Background AutoSave';
const SYSTEM_USER_ID = '7fd2b0e5-17b4-433c-9f45-93a5243d452e'; // TODO! move to .env or config

const appState = useAppState();
const alert = useAlert();
const activeStudyStore = useActiveStudyStore();

const browser
  = useTemplateRef<InstanceType<typeof SavedAnalysisStateBrowser>>('browser');
const showDialog = ref(false);
const saveMode = ref(false);
const studyInstanceUid = ref('');
const askConfirmationOverwrite = ref(false);
const askConfirmationLoad = ref(false);
const askConfirmationDelete = ref(false);

const idToConfirm = ref<string | null>(null);
const showSaveWarningDialog = ref(false);
const targetElement = ref('');
let warningResolve: ((value: boolean) => void) | null = null;
let warningReject: (() => void) | null = null;
const newTitle = ref('');
let resolve: ((id: string) => void) | null = null;
let reject: (() => void) | null = null;

async function invokeSave(_studyInstanceUid: string) {
  studyInstanceUid.value = _studyInstanceUid;
  saveMode.value = true;
  newTitle.value = '';
  showDialog.value = true;
  return new Promise<string>((res, rej) => {
    resolve = res;
    reject = rej;
  });
}

async function invokeLoad(_studyInstanceUid: string) {
  studyInstanceUid.value = _studyInstanceUid;
  saveMode.value = false;
  showDialog.value = true;
  return new Promise<string>((res, rej) => {
    resolve = res;
    reject = rej;
  });
}

async function invokeSaveWarning(
  _targetElement: string,
  _studyInstanceUid: string,
) {
  targetElement.value = _targetElement;
  studyInstanceUid.value = _studyInstanceUid;
  showSaveWarningDialog.value = true;
  return new Promise<boolean>((res, rej) => {
    warningResolve = res;
    warningReject = rej;
  });
}

function resolveDialog(id: string) {
  showDialog.value = false;
  resolve?.(id);
  resolve = null;
  reject = null;
}

function cancelDialog() {
  showDialog.value = false;
  reject?.();
  resolve = null;
  reject = null;
}

async function handleRowClick(item: Item) {
  idToConfirm.value = item.id;
  if (saveMode.value) {
    newTitle.value = item.title;
    askConfirmationOverwrite.value = true;
    return;
  }
  askConfirmationLoad.value = true;
}

async function handleClickDelete(item: Item) {
  idToConfirm.value = item.id;
  askConfirmationDelete.value = true;
}

async function storeContours(
  analysisStateID: string,
  contourContainerStore: any,
) {
  const imageUIDs = Object.keys(contourContainerStore);
  const userID = session.value?.user.id;
  if (!userID) {
    alert.error('No user logged in');
    return;
  }
  imageUIDs.forEach((imageUID) => {
    const contours = contourContainerStore[imageUID];
    const contoursJson = JSON.stringify(contours);
    const compressed = LZString.compressToUTF16(contoursJson);

    supabase
      .from('analysis_state_contour')
      .insert({
        analysis_state_id: analysisStateID,
        image_instance_uid: imageUID,
        contours: compressed,
      })
      .select('id')
      .single()
      .then((result) => {
        if (result.error) {
          alert.error(
            `Error saving contour for ${imageUID}: ${getMessage(result.error)}`,
          );
        }
      });
  });
}

async function saveCompletedAnalysis(analysisState: any) {
  if (!session.value || !studyInstanceUid.value) return;

  const {
    studyData,
    createdBy,
    createdAt,
    appVersion,
    studyInstanceUID,
    contourContainerStore,
    classification,
    viewportIndexMap,
    analysisResult,
    report,
  } = analysisState;

  try {
    // Delete existing "Completed Analysis" for this study+user (only one allowed)
    const { data: existingCompleted, error: searchError } = await supabase
      .from('analysis_state')
      .select('id')
      .eq('study_instance_uid', studyInstanceUid.value)
      .eq('user', session.value.user.id)
      .eq('state->>title', SAVE_WITH_RESULT_TITLE)
      .limit(1);

    if (searchError) throw searchError;

    if (existingCompleted && existingCompleted.length > 0) {
      const oldId = existingCompleted[0].id;
      // Delete contours, result, then the state itself
      await supabase
        .from('analysis_state_contour')
        .delete()
        .eq('analysis_state_id', oldId);
      await supabase
        .from('analysis_state_result')
        .delete()
        .eq('analysis_state_id', oldId);
      await supabase.from('analysis_state').delete().eq('id', oldId);
    }

    const state = {
      title: SAVE_WITH_RESULT_TITLE,
      createdBy,
      createdAt,
      appVersion,
      studyInstanceUID,
      studyData,
      viewportIndexMap,
      classification,
      hasResults: true,
      report,
    };

    // Create new completed analysis save
    const { data, error } = await supabase
      .from('analysis_state')
      .insert({
        org: (appState.currentUserOrgs as any[])[0].id,
        user: session.value.user.id,
        study_instance_uid: studyInstanceUID,
        state,
      })
      .select('id')
      .single();
    if (error) throw error;
    if (!data) throw new Error('No data returned');

    // Store contours separately for this completed analysis
    storeContours(data.id, contourContainerStore);

    // Store the analysis result in the dedicated table
    const { error: resultError } = await supabase
      .from('analysis_state_result')
      .insert({
        analysis_state_id: data.id,
        study_ocg_result: analysisResult,
      });
    if (resultError) throw resultError;
  }
  catch (error) {
    console.error('Failed to save completed analysis:', error);
  }
}

async function saveWithTitle(title: string) {
  const analysisState = appState.serializeAnalysisState(title);
  const {
    studyData,
    title: t,
    createdBy,
    createdAt,
    appVersion,
    studyInstanceUID,
    contourContainerStore,
    classification,
    report,
  } = analysisState;

  const state = {
    title: t,
    createdBy,
    createdAt,
    appVersion,
    studyInstanceUID,
    studyData,
    classification,
    report,
  };

  if (!session.value) {
    throw new Error('No user logged in');
  }
  const { data, error } = await supabase
    .from('analysis_state')
    .insert({
      org: (appState.currentUserOrgs as any[])[0].id,
      user: session.value.user.id,
      study_instance_uid: state.studyInstanceUID,
      state: state as unknown as Json,
    })
    .select('id')
    .single();
  if (!data) throw new Error('No data returned');
  if (error) throw error;
  const analysisStateID = data.id;
  storeContours(analysisStateID, contourContainerStore);
  activeStudyStore.hasSavedState = true;
  return analysisStateID;
}

async function newNameEntered() {
  if (newTitle.value.trim() === '' || !session.value) {
    return;
  }
  try {
    const analysisStateID = await saveWithTitle(newTitle.value.trim());
    resolveDialog(analysisStateID);
  }
  catch (error) {
    alert.error(`Error saving analysis state: ${getMessage(error)}`);
  }
}

async function confirmOverwrite() {
  askConfirmationOverwrite.value = false;
  if (idToConfirm.value === null) {
    return;
  }
  const analysisState = appState.serializeAnalysisState(newTitle.value.trim());
  const { contourContainerStore, ...state } = analysisState;

  try {
    // First delete existing contours for this analysis state
    const { error: deleteError } = await supabase
      .from('analysis_state_contour')
      .delete()
      .eq('analysis_state_id', idToConfirm.value);
    if (deleteError) throw deleteError;

    // Update the analysis state
    const { error } = await supabase
      .from('analysis_state')
      .update({
        state: state as unknown as Json,
        created_at: new Date().toISOString(),
      })
      .eq('id', idToConfirm.value);
    if (error) throw error;

    // Store new contours
    storeContours(idToConfirm.value, contourContainerStore);
    activeStudyStore.hasSavedState = true;

    resolveDialog(idToConfirm.value);
  }
  catch (error) {
    alert.error(`Error saving analysis state: ${getMessage(error)}`);
  }
  alert.info(`Successfully overwrote save.`);
}

async function loadGeneralAnalysisState(analysisStateID: string) {
  if (!analysisStateID) {
    return;
  }
  alert.info('Saved state found, loading...');
  try {
    // Load the analysis state
    const { data, error } = await supabase
      .from('analysis_state')
      .select('state')
      .eq('id', analysisStateID)
      .single();
    if (error) throw error;
    const { state } = data;
    if (!state) {
      throw new Error('Analysis state data is null');
    }

    // Load the contours for user saves
    const { data: contoursData, error: contoursError } = await supabase
      .from('analysis_state_contour')
      .select('image_instance_uid, contours')
      .eq('analysis_state_id', analysisStateID);
    if (contoursError) throw contoursError;

    // Reconstruct the contourContainerStore
    const contourContainerStore: Record<string, any> = {};
    contoursData?.forEach((row) => {
      if (!row.image_instance_uid) {
        alert.warning('Missing image_instance_uid in contour data row');
        return;
      }
      if (!row.contours) {
        alert.warning(
          `Contour data is null for image ${row.image_instance_uid}`,
        );
        return;
      }
      const decompressed = LZString.decompressFromUTF16(row.contours);
      if (decompressed === null) {
        throw new Error(
          `Failed to decompress contours for image ${row.image_instance_uid}`,
        );
      }
      contourContainerStore[row.image_instance_uid] = JSON.parse(decompressed);
    });

    let analysisResult = null;

    if ((state as any).hasResults) {
      const { data: resultData, error: resultError } = await supabase
        .from('analysis_state_result')
        .select('study_ocg_result')
        .eq('analysis_state_id', analysisStateID)
        .single();
      if (resultError) throw resultError;
      if (resultData) {
        analysisResult = resultData.study_ocg_result;
      }
    }

    // Combine state with contourContainerStore
    appState.loadAnalysisState(SerializedAnalysisStateSchema.parse({
      ...(data.state as Record<string, any>),
      contourContainerStore,
      analysisResult, // Placeholder, will be populated below if exists
    }));

    activeStudyStore.hasSavedState = true;
    alert.info(`Successfully loaded save.`);
  }
  catch (error) {
    alert.error(`Error fetching analysis state: ${getMessage(error)}`);
  }
}

// Ideally moved to appState.loadAnalysisState. Need to keep here for now
// as a system save does not have all the bells and whistles of a regular save
// TODO: fix in future
async function loadClassification(targetID: string) {
  try {
    // Also fetch classification results
    const { data, error } = await supabase
      .from('analysis_state')
      .select('state')
      .eq('id', targetID)
      .single();
    if (error) {
      alert.error(`Error fetching analysis state: ${getMessage(error)}`);
      return;
    }

    const { state } = data;
    const { classification } = state as any;
    // Backend stores the classification information based on seriesInstanceUID. Front-end based on seriesIdx.
    // To prevent missmatch (especially for multiframe), we need to remap here.
    seriesClassification.adjustClassification(classification);
    seriesClassification.applyDefaultViewportMapping();
  }
  catch (error) {
    alert.error(`Error applying classification: ${getMessage(error)}`);
  }
}

// TODO: This is redundant and should be merged to lead a state, currently system contours do not have
// all the info that comes from contourContainerStore.deserialize so keeping to avoid breaking changes, but
//  should unify the loading process for system and user saves
async function loadSystemAnalysisState(studyInstanceUID?: string) {
  if (!studyInstanceUID && idToConfirm.value === null) {
    return;
  }
  // If the user has not provided a studyInstanceUID, use the local idToConfirm
  let targetId: string | null = '';
  if (
    studyInstanceUID === undefined
    || studyInstanceUID === ''
    || studyInstanceUID === null
  ) {
    targetId = idToConfirm.value;
  }
  else {
    if (session.value) {
      const { data: autoSaves } = await supabase
        .from('analysis_state')
        .select('id')
        .eq('study_instance_uid', studyInstanceUID)
        .eq('user', session.value.user.id)
        .eq('state->>title', AUTOSAVE_TITLE)
        .order('created_at', { ascending: false })
        .limit(1);

      if (autoSaves && autoSaves.length > 0) {
        targetId = autoSaves[0].id;
        await loadGeneralAnalysisState(targetId);
        return;
      }
    }

    // Fetch the latest system save for the given studyInstanceUID
    const { data, error } = await supabase
      .from('analysis_state')
      .select('id')
      .eq('study_instance_uid', studyInstanceUID)
      .eq('user', SYSTEM_USER_ID) // System User ID
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    if (error || !data) {
      alert.warning(`No system save found for the given study.`);
      return;
    }
    targetId = data.id;
  }

  if (!targetId) {
    alert.info('No existing system analysis state found.');
    return;
  }

  alert.info('System save found, loading contours...');

  await loadClassification(targetId);

  try {
    // Load the contours for system save
    const { data: contoursData, error: contoursError } = await supabase
      .from('analysis_state_contour')
      .select('image_instance_uid, contours')
      .eq('analysis_state_id', targetId);
    if (contoursError) throw contoursError;

    // Reconstruct contours as batches for system saves
    const allContoursBatches: BatchResult[] = [];
    contoursData?.forEach((row) => {
      if (!row.image_instance_uid) {
        alert.warning('Missing image_instance_uid in contour data row');
        return;
      }
      if (!row.contours) {
        alert.warning(
          `Contour data is null for image ${row.image_instance_uid}`,
        );
        return;
      }
      const decompressed = LZString.decompressFromUTF16(row.contours);
      if (decompressed === null) {
        throw new Error(
          `Failed to decompress contours for image ${row.image_instance_uid}`,
        );
      }
      allContoursBatches.push({
        imageUID: row.image_instance_uid,
        contours: JSON.parse(decompressed),
      });
    });

    const segAgent = useBackendSegmentation();
    segAgent.addContourBatch(allContoursBatches);

    resolveDialog(targetId);

    activeStudyStore.hasSavedState = true;
    alert.info(`Successfully loaded system save.`);
  }
  catch (error) {
    alert.error(`Error loading system analysis state: ${getMessage(error)}`);
  }
}

async function confirmLoad() {
  askConfirmationLoad.value = false;
  if (idToConfirm.value === null) {
    return;
  }
  alert.info('Saved state found, loading...');
  try {
    // Load the analysis state
    const { data, error } = await supabase
      .from('analysis_state')
      .select('state')
      .eq('id', idToConfirm.value)
      .single();
    if (error) throw error;
    const { state } = data;
    if (!state) {
      throw new Error('Analysis state data is null');
    }
    const isSystemSave = (state as any).appVersion === SYSTEM_SAVE_VERSION;

    if (isSystemSave) {
      await loadSystemAnalysisState();
      return;
    }

    // Load the contours for user saves
    const { data: contoursData, error: contoursError } = await supabase
      .from('analysis_state_contour')
      .select('image_instance_uid, contours')
      .eq('analysis_state_id', idToConfirm.value);
    if (contoursError) throw contoursError;

    // Reconstruct the contourContainerStore
    const contourContainerStore: Record<string, any> = {};
    contoursData?.forEach((row) => {
      if (!row.image_instance_uid) {
        alert.warning('Missing image_instance_uid in contour data row');
        return;
      }
      if (!row.contours) {
        alert.warning(
          `Contour data is null for image ${row.image_instance_uid}`,
        );
        return;
      }
      const decompressed = LZString.decompressFromUTF16(row.contours);
      if (decompressed === null) {
        throw new Error(
          `Failed to decompress contours for image ${row.image_instance_uid}`,
        );
      }
      contourContainerStore[row.image_instance_uid] = JSON.parse(decompressed);
    });

    let analysisResult = null;

    if ((state as any).hasResults) {
      const { data: resultData, error: resultError } = await supabase
        .from('analysis_state_result')
        .select('study_ocg_result')
        .eq('analysis_state_id', idToConfirm.value)
        .single();
      if (resultError) throw resultError;
      if (resultData) {
        analysisResult = resultData.study_ocg_result;
      }
    }

    // Combine state with contourContainerStore
    appState.loadAnalysisState(SerializedAnalysisStateSchema.parse({
      ...(data.state as Record<string, any>),
      contourContainerStore,
      analysisResult, // Placeholder, will be populated below if exists
    }));

    resolveDialog(idToConfirm.value);
    activeStudyStore.hasSavedState = true;
    alert.info(`Successfully loaded save.`);
  }
  catch (error) {
    alert.error(`Error fetching analysis state: ${getMessage(error)}`);
  }
}

async function confirmDelete() {
  askConfirmationDelete.value = false;
  if (idToConfirm.value === null) {
    return;
  }
  try {
    const { count, error } = await supabase
      .from('analysis_state')
      .delete({ count: 'exact' })
      .eq('id', idToConfirm.value);
    if (error) throw error;
    if (count === 0) throw new Error('No rows deleted');
  }
  catch (error) {
    alert.error(`Error deleting analysis state: ${getMessage(error)}`);
    return;
  }
  alert.info(`Successfully deleted save.`);

  browser.value?.reload();
}

function cancelWarning() {
  showSaveWarningDialog.value = false;
  warningReject?.();
  warningResolve = null;
  warningReject = null;
}

async function continueWithoutSaving() {
  showSaveWarningDialog.value = false;
  warningResolve?.(true);
  warningResolve = null;
  warningReject = null;
}

async function createAutoSave() {
  showSaveWarningDialog.value = false;
  const autoTitle = `AutoSave ${new Date().toISOString()}`;
  try {
    await saveWithTitle(autoTitle);
    warningResolve?.(true);
    alert.info(`Successfully created save: "${autoTitle}"`);
  }
  catch (error) {
    alert.error(`Error auto-saving: ${getMessage(error)}`);
    warningReject?.();
  }
  warningResolve = null;
  warningReject = null;
}

async function backgroundAutosave(targetStudyInstanceUid?: string) {
  if (targetStudyInstanceUid) {
    studyInstanceUid.value = targetStudyInstanceUid;
  }
  if (!session.value || !studyInstanceUid.value) {
    console.warn('Cannot autosave: no session or study instance UID');
    return;
  }

  try {
    // Search for existing autosave with our title
    const { data: existingSaves, error: searchError } = await supabase
      .from('analysis_state')
      .select('id')
      .eq('study_instance_uid', studyInstanceUid.value)
      .eq('user', session.value.user.id)
      .eq('state->>title', AUTOSAVE_TITLE)
      .limit(1);

    if (searchError) throw searchError;
    const analysisState = appState.serializeAnalysisState(AUTOSAVE_TITLE);
    const { contourContainerStore, analysisResult, ...state } = analysisState;

    // If there's an analysis result, create/replace the "Completed Analysis" save
    if (analysisResult) {
      await saveCompletedAnalysis(analysisState);
      return; // Don't need to do the rest of the autosave if we just created a completed analysis save, as that already includes all the current state info
    }

    if (existingSaves && existingSaves.length > 0) {
      // Update existing autosave
      const autosaveId = existingSaves[0].id;

      // Delete existing contours
      const { error: deleteError } = await supabase
        .from('analysis_state_contour')
        .delete()
        .eq('analysis_state_id', autosaveId);
      if (deleteError) throw deleteError;

      // Update the analysis state
      const { error: updateError } = await supabase
        .from('analysis_state')
        .update({
          state: state as unknown as Json,
          created_at: new Date().toISOString(),
        })
        .eq('id', autosaveId);
      if (updateError) throw updateError;

      // Store new contours
      storeContours(autosaveId, contourContainerStore);
    }
    else {
      // Create new autosave
      const { data, error } = await supabase
        .from('analysis_state')
        .insert({
          org: (appState.currentUserOrgs as any[])[0].id,
          user: session.value.user.id,
          study_instance_uid: state.studyInstanceUID,
          state: state as unknown as Json,
        })
        .select('id')
        .single();
      if (error) throw error;
      if (!data) throw new Error('No data returned');

      storeContours(data.id, contourContainerStore);
    }
    activeStudyStore.hasSavedState = true;
  }
  catch (error) {
    console.error('Background autosave failed:', error);
    // Don't show user alerts for background saves to avoid interrupting workflow
  }
}

async function loadLatestSave(studyInstanceUID: string) {
  if (!studyInstanceUID) return;

  try {
    const { data, error } = await supabase
      .from('analysis_state')
      .select('id, state')
      .eq('study_instance_uid', studyInstanceUID)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) throw error;
    if (!data || data.length === 0) {
      return;
    }

    const { id, state } = data[0];
    const isSystemSave = (state as any).appVersion === SYSTEM_SAVE_VERSION;

    if (isSystemSave) {
      idToConfirm.value = id;
      await loadSystemAnalysisState();
      idToConfirm.value = null;
    }
    else {
      await loadGeneralAnalysisState(id);
    }
  }
  catch (error) {
    console.error(`Error loading latest save: ${getMessage(error)}`);
  }
}

defineExpose({
  invokeSave,
  invokeLoad,
  invokeSaveWarning,
  loadSystemAnalysisState,
  backgroundAutosave,
  loadLatestSave,
});
</script>

<template>
  <v-dialog
    v-model="showDialog"
    max-width="80vw"
    @update:model-value="showDialog || cancelDialog()"
  >
    <v-card
      :title="
        saveMode
          ? 'Save Analysis State: Enter new name or select an existing entry to overwrite'
          : 'Choose analysis state to load'
      "
    >
      <v-card-text>
        <v-text-field
          v-if="saveMode"
          v-model="newTitle"
          clearable
          placeholder="New name"
          @keyup.enter="newNameEntered"
          @click:clear="newTitle = ''"
        />
        <SavedAnalysisStateBrowser
          ref="browser"
          :study-instance-uid="studyInstanceUid"
          :user-uid="saveMode ? session?.user.id : undefined"
          @clicked="handleRowClick"
          @remove-clicked="handleClickDelete"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn v-if="saveMode" @click="newNameEntered">
          Save
        </v-btn>
        <v-btn @click="cancelDialog">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="askConfirmationOverwrite" max-width="50vw">
    <v-card>
      <v-card-title>Please confirm</v-card-title>
      <v-card-text>
        <p>Are you sure you want to overwrite the existing analysis state?</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="askConfirmationOverwrite = false">
          Cancel
        </v-btn>
        <v-btn @click="confirmOverwrite">
          Overwrite
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="askConfirmationLoad" max-width="50vw">
    <v-card>
      <v-card-title>Please confirm</v-card-title>
      <v-card-text>
        <p>Continue loading? This will replace your current analysis state.</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="askConfirmationLoad = false">
          Cancel
        </v-btn>
        <v-btn @click="confirmLoad">
          Load
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="askConfirmationDelete" max-width="50vw">
    <v-card>
      <v-card-title>Please confirm</v-card-title>
      <v-card-text>
        <p>Are you sure you want to delete this analysis state?</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="askConfirmationDelete = false">
          Cancel
        </v-btn>
        <v-btn @click="confirmDelete">
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="showSaveWarningDialog" max-width="50vw">
    <v-card>
      <v-card-title>Please confirm</v-card-title>
      <v-card-text>
        <p>
          You have unsaved changes in {{ targetElement }}. We would recommend
          saving your work before continuing.
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="cancelWarning">
          Cancel
        </v-btn>
        <v-btn @click="continueWithoutSaving">
          Continue Without Saving
        </v-btn>
        <v-btn @click="createAutoSave">
          Create AutoSave
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped></style>
